import { useMemo } from "react";
import * as React from "react";
import { HorizontalShopCardList } from "@components/HorizontalShopCardList";
import CommonDataState from "@hooks/useCommonData";
import { paths } from "@libs/paths";
import { getAreaStaticURL } from "@libs/utils/areaStaticUrl";
import { GTMEvent, GTMEvents } from "@libs/utils/gtm";
import {
  AreaTypeEnum,
  useNeighborShopsList_NeighborShopsQuery as useNeighborShopsQuery,
} from "./__generated__/queries";
import styles from "./index.module.css";

type NeighborShopListProps = {
  shopUuid: string;
};

export const NeighborShopList: React.FC<NeighborShopListProps> = ({
  shopUuid,
}) => {
  const { data: commonData } = CommonDataState.useContainer();
  const { data, error } = useNeighborShopsQuery({
    variables: { shopUuid: shopUuid },
  });
  const neighborShops = data?.neighborShops;
  if (error) {
    console.error(error);
  }

  const staticRepresentation = useMemo(() => {
    if (!commonData) {
      return null;
    }
    if (!(neighborShops?.areaId && neighborShops?.areaType)) {
      return null;
    }
    return getAreaStaticURL(
      commonData.masterPrefectures,
      neighborShops.areaType,
      neighborShops.areaId
    );
  }, [commonData, neighborShops]);

  const dynamicRepresentation = useMemo(() => {
    if (!(neighborShops?.areaId && neighborShops?.areaType)) {
      return null;
    }

    const property = {
      [AreaTypeEnum.MasterArea]: { areaIds: [neighborShops?.areaId] },
      [AreaTypeEnum.CombinedArea]: { combinedAreaIds: [neighborShops?.areaId] },
      [AreaTypeEnum.MasterPrefecture]: { prefectureId: neighborShops?.areaId },
    }[neighborShops.areaType];

    return paths.shops(property);
  }, [neighborShops]);

  const onShopClick = (index: number): void => {
    if (!(neighborShops?.areaId && neighborShops?.areaType)) {
      return;
    }
    GTMEvent(
      GTMEvents.neighborShopListPressed(
        neighborShops.areaId,
        neighborShops.areaType,
        true,
        `/shops/${shopUuid}`,
        `/shops/${neighborShops.shops[index].uuid}`
      )
    );
  };
  const onHasMoreButtonClick = (): void => {
    if (!(neighborShops?.areaId && neighborShops?.areaType)) {
      return;
    }
    GTMEvent(
      GTMEvents.neighborShopListPressed(
        neighborShops.areaId,
        neighborShops.areaType,
        false,
        `/shops/${shopUuid}`,
        staticRepresentation ?? dynamicRepresentation ?? ""
      )
    );
  };

  return (
    <>
      {neighborShops && neighborShops.shops.length > 0 && (
        <section className={styles.neighboringShops}>
          <h1 className={`${styles.neighboringShopsTitle} c-contentTitle`}>
            {neighborShops.areaText}
          </h1>
          <div className={styles.neighboringShopsList}>
            <HorizontalShopCardList
              onShopClick={onShopClick}
              onMoreButtonClick={onHasMoreButtonClick}
              shops={neighborShops.shops}
              hasMoreButton={neighborShops.hasMore}
              moreText="もっと見る"
              moreButtonLinkPath={
                staticRepresentation ?? dynamicRepresentation ?? ""
              }
            />
          </div>
        </section>
      )}
    </>
  );
};
